// src/pages/RecruiterTerms.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const RecruiterTerms = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-user-tie text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Recruiter Terms & Conditions
                </h1>
                <p className="text-gray-600">
                    Terms specific to recruiters and companies using GradPath
                </p>
                <p className="text-sm text-gray-500 mt-2">Last updated: June 27, 2026</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p className="text-yellow-800 text-sm">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        These terms apply specifically to recruiters and companies using GradPath to find talent.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-check-circle text-gradpath-green text-lg"></i>
                        Acceptance of Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        By creating a recruiter account and using GradPath's recruitment services, you agree to be bound by these Recruiter Terms & Conditions, our main Terms & Conditions, Privacy Policy, and all applicable laws.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-briefcase text-gradpath-green text-lg"></i>
                        Job Posting Legitimacy
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        All job postings on GradPath must be:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Genuine:</strong> Real job opportunities that are currently available</li>
                        <li><strong>Accurate:</strong> Correct job descriptions, requirements, and salary information</li>
                        <li><strong>Non-discriminatory:</strong> Compliant with South Africa's Employment Equity Act</li>
                        <li><strong>Current:</strong> Updated regularly to reflect availability</li>
                    </ul>
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700">
                            <i className="fas fa-exclamation-circle mr-2"></i>
                            <strong>Prohibited:</strong> Posting false, misleading, or fraudulent job advertisements is strictly prohibited and will result in immediate account termination.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-ban text-gradpath-green text-lg"></i>
                        Account Termination for False Job Ads
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Reports of false job advertisements will result in immediate investigation. If a recruiter is found to have posted fraudulent, misleading, or non-existent job opportunities:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>The account will be permanently terminated</li>
                        <li>All job postings will be removed</li>
                        <li><strong>No refund will be issued</strong> for any remaining tokens or unused services</li>
                        <li>Legal action may be pursued</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-coins text-gradpath-green text-lg"></i>
                        Token Purchases & Refunds
                    </h2>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                        <p className="text-gray-700 leading-relaxed">
                            <strong>Token Purchases:</strong> Recruiters purchase tokens to access premium features including:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                            <li>Unlocking candidate contact information</li>
                            <li>Enhanced job posting visibility</li>
                            <li>Access to the full talent database</li>
                            <li>Priority support</li>
                        </ul>
                        <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-sm text-red-700 font-bold">
                                <i className="fas fa-exclamation-circle mr-2"></i>
                                NO REFUND POLICY: All token purchases are final and non-refundable.
                            </p>
                            <p className="text-sm text-red-600 mt-1">
                                We do not offer refunds for unused tokens, regardless of the reason.
                            </p>
                        </div>
                        <p className="text-gray-700 leading-relaxed mt-3">
                            <strong>Token Expiry:</strong> Tokens do not expire and remain in your account until used.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            <strong>Price Changes:</strong> Token prices are subject to change with 30 days' notice.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-user-shield text-gradpath-green text-lg"></i>
                        Candidate Data Usage
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Recruiters are granted access to candidate information for legitimate recruitment purposes only. By using GradPath, you agree:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Not to share</strong> candidate data with third parties</li>
                        <li><strong>Not to sell</strong> candidate data for any purpose</li>
                        <li><strong>Not to use</strong> candidate data for spam or harassment</li>
                        <li><strong>To comply</strong> with POPIA when processing candidate data</li>
                        <li><strong>To delete</strong> candidate data when no longer needed for recruitment</li>
                    </ul>
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700">
                            <i className="fas fa-info-circle mr-2"></i>
                            Candidate data is provided for recruitment purposes only. Misuse of candidate data will result in account termination and possible legal action.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-gavel text-gradpath-green text-lg"></i>
                        Compliance with Laws
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Recruiters must comply with all applicable South African laws and regulations, including but not limited to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>POPIA:</strong> Protection of Personal Information Act</li>
                        <li><strong>Employment Equity Act:</strong> Non-discrimination in hiring</li>
                        <li><strong>Labour Relations Act:</strong> Fair labour practices</li>
                        <li><strong>Basic Conditions of Employment Act:</strong> Fair employment conditions</li>
                        <li><strong>Consumer Protection Act:</strong> Fair business practices</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-flag text-gradpath-green text-lg"></i>
                        Reporting Violations
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        If you suspect a recruiter is violating these terms:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Report to <a href="mailto:compliance@gradpath.co.za" className="text-gradpath-green hover:underline">compliance@gradpath.co.za</a></li>
                        <li>Include job posting links and evidence</li>
                        <li>All reports are investigated promptly</li>
                        <li>Reports are kept confidential</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-edit text-gradpath-green text-lg"></i>
                        Changes to These Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We may modify these Recruiter Terms at any time. Changes become effective immediately upon posting. Your continued use of the Platform after changes constitutes acceptance of the updated terms.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Contact Information
                    </h2>
                    <p className="text-gray-700 text-sm">
                        For questions about these Recruiter Terms, please contact:
                    </p>
                    <p className="text-gray-700 text-sm mt-2">
                        <strong>Support:</strong> <a href="mailto:recruiters@gradpath.co.za" className="text-gradpath-green hover:underline">recruiters@gradpath.co.za</a><br />
                        <strong>Compliance:</strong> <a href="mailto:compliance@gradpath.co.za" className="text-gradpath-green hover:underline">compliance@gradpath.co.za</a>
                    </p>
                </section>

                <div className="text-center pt-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-gradpath-green hover:underline">
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default RecruiterTerms;